/**
 * Folds `voice.occupancy` into the directory cache.
 *
 * The event carries the FULL occupant list, so this is a replacement rather
 * than a merge — no join/leave arithmetic can drift, which is the whole reason
 * the server sends it that way.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent } from '@cyphex/types';
import type { VoiceChannelSummary } from '@cyphex/types/voice';
import { VOICE_KEYS } from '../api/queryKeys';
import { playJoinChime, playLeaveChime } from './chime';
import { voicePrefs } from '../store/voicePrefs';
import { voiceSessionStore } from '../store/voiceSessionStore';

export function applyEventToVoice(
  client: QueryClient,
  event: RealtimeEvent,
  viewerId: string | null,
): void {
  if (event.type !== 'voice.occupancy') return;

  let matched = false;
  // Both the workspace-wide list and any per-team list hold this channel.
  for (const key of [VOICE_KEYS.directory(null), VOICE_KEYS.directory(event.teamId)]) {
    client.setQueryData<VoiceChannelSummary[]>(key, (previous) => {
      if (previous === undefined) return previous;
      let found = false;
      const next = previous.map((entry) => {
        if (entry.channel.id !== event.channelId) return entry;
        found = true;
        return {
          ...entry,
          occupants: event.occupants,
          isJoined: event.occupants.some((occupant) => occupant.user.id === viewerId),
        };
      });
      matched = matched || found;
      return found ? next : previous;
    });
  }

  // A voice channel we have never seen (just created, or created while this tab
  // was open). Ask for the directory again rather than invent a channel row.
  if (!matched) {
    void client.invalidateQueries({ queryKey: VOICE_KEYS.all });
    return;
  }

  maybeChime(event.channelId, event.change, viewerId);
}

/**
 * The chime plays only for the room you are IN, and never for your own arrival.
 * A workspace-wide doorbell would be intolerable in an org with twenty rooms.
 */
function maybeChime(
  channelId: string,
  change: { userId: string; kind: 'joined' | 'left' | 'updated' } | null,
  viewerId: string | null,
): void {
  if (change === null || change.kind === 'updated') return;
  if (change.userId === viewerId) return;
  if (!voicePrefs.get().chimes) return;
  if (voiceSessionStore.get().channelId !== channelId) return;
  if (change.kind === 'joined') playJoinChime();
  else playLeaveChime();
}
